/**
 * Directives — Procedural Daily Match Directives Engine.
 * 
 * Generates 3 deterministic daily challenges based on calendar date (YYYY-MM-DD):
 * - Tier 1: Routine (Accessible, cumulative or single match) -> +30 Fugazi, +3 Rank Pts
 * - Tier 2: Tactical (Strategic mastery & territory)       -> +50 Fugazi, +5 Rank Pts
 * - Tier 3: Apex (Skill challenge & clutch play)           -> +80 Fugazi, +8 Rank Pts
 * 
 * Daily Jackpot: Completing all 3 awards +100 Fugazi.
 * Zero-GC tracking during lockstep simulation.
 */

import { storageAdapter } from './storageAdapter.js';
import { wallet } from './wallet.js';
import { progress } from './progress.js';
import { inventory } from './inventory.js';

// Deterministic 32-bit PRNG (Mulberry32)
function createPrng(seed) {
  let s = seed | 0;
  return function next() {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

export const DIRECTIVE_TIERS = {
  TIER_1: 1,
  TIER_2: 2,
  TIER_3: 3
};

export const DAILY_JACKPOT_FUGAZI = 100;
export const DAILY_JACKPOT_XP = 50;

// Daily streak (docs/DAILY_QUESTS_EXPANSION_PLAN_2026-09-24.md): one day counts
// once its Daily Jackpot is claimed. Milestones repeat every STREAK_CYCLE_DAYS.
export const STREAK_CYCLE_DAYS = 14;
export const STREAK_MILESTONES = [
  { day: 3, kind: 'fugazi', amount: 180 },
  { day: 7, kind: 'packs', amount: 2 },
  { day: 14, kind: 'packs', amount: 5 }
];
export const STREAK_AT_RISK_HOURS = 4;

/**
 * Returns the milestones for cycle C (1-indexed: cycle 1 = days 1..14, cycle 2 = days 15..28, etc.).
 * Day 3 (+3): 180 Fugazi
 * Day 7 (+7): 2 Booster Packs
 * Day 14 (+14): 5 + (cycle - 1) Booster Packs (unlimited scaling: 5, 6, 7, 8, ...)
 */
export function getStreakMilestonesForCycle(cycle = 1) {
  const c = Math.max(1, cycle);
  const baseDay = (c - 1) * STREAK_CYCLE_DAYS;
  const packs14 = 5 + (c - 1);
  return [
    { day: baseDay + 3, dayInCycle: 3, kind: 'fugazi', amount: 180 },
    { day: baseDay + 7, dayInCycle: 7, kind: 'packs', amount: 2 },
    { day: baseDay + 14, dayInCycle: 14, kind: 'packs', amount: packs14 }
  ];
}

function parseDateKey(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days from dateA to dateB ('YYYY-MM-DD'); negative when B is earlier. */
export function daysBetween(dateA, dateB) {
  return Math.round((parseDateKey(dateB) - parseDateKey(dateA)) / 86400000);
}

export function addDays(dateStr, n) {
  const d = new Date(parseDateKey(dateStr) + n * 86400000);
  const pad = v => String(v).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** ISO-8601 week key ('YYYY-Www', weeks start Monday, week 1 holds the year's first Thursday). */
export function getISOWeekString(dateStr) {
  const d = new Date(parseDateKey(dateStr));
  const dayNum = d.getUTCDay() || 7; // Mon=1 .. Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // shift to this week's Thursday
  const isoYear = d.getUTCFullYear();
  const yearStart = Date.UTC(isoYear, 0, 1);
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

/** Position 0..14 of a streak inside its current milestone cycle (0 = no streak). */
export function streakCyclePosition(streak) {
  return streak <= 0 ? 0 : ((streak - 1) % STREAK_CYCLE_DAYS) + 1;
}

// Directive Templates Pool
const TEMPLATES_TIER_1 = [
  {
    type: 'DEAL_DAMAGE',
    title: 'Firepower Calibration',
    desc: (tgt) => `Deal a cumulative total of ${tgt.toLocaleString()} damage.`,
    isCumulative: true,
    targetMin: 3000,
    targetMax: 6000,
    step: 500,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'WIN_MATCHES',
    title: 'Combat Supremacy',
    desc: (tgt) => `Win ${tgt} combat ${tgt === 1 ? 'engagement' : 'engagements'}.`,
    isCumulative: true,
    targetMin: 1,
    targetMax: 2,
    step: 1,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'PLAY_CARDS',
    title: 'Fleet Mobilization',
    desc: (tgt) => `Deploy a total of ${tgt} units or tactical actions.`,
    isCumulative: true,
    targetMin: 15,
    targetMax: 25,
    step: 5,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'BODY_COUNT',
    title: 'Body Count',
    desc: (tgt) => `Destroy ${tgt} enemy units.`,
    isCumulative: true,
    targetMin: 40,
    targetMax: 100,
    step: 20,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'MERGE_PROTOCOL',
    title: 'Merge Protocol',
    desc: (tgt) => `Perform ${tgt} ${tgt === 1 ? 'meld' : 'melds'}.`,
    isCumulative: true,
    targetMin: 1,
    targetMax: 3,
    step: 1,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'FACTION_LOYALTY',
    title: 'Faction Loyalty',
    // Only the currently active rosters; the race is drawn per day and stored on the directive.
    races: ['Human', 'Sul'],
    desc: (tgt, race) => `Deploy ${tgt} ${race} cards.`,
    isCumulative: true,
    targetMin: 10,
    targetMax: 20,
    step: 5,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'AIR_SUPERIORITY',
    title: 'Air Superiority',
    desc: (tgt) => `Destroy ${tgt} enemy air units.`,
    isCumulative: true,
    targetMin: 6,
    targetMax: 15,
    step: 3,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'AIR_WING_SURGE',
    title: 'Air Superiority Surge',
    desc: () => 'Deploy 3 Air units within 5 seconds.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'CONVENTIONAL_WAR',
    title: 'Conventional Warfare',
    desc: () => 'Win a match without deploying your Superweapon.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  },
  {
    type: 'SPELL_MASTERY',
    title: 'Tactical Support',
    desc: (tgt) => `Cast ${tgt} Tactical Spells in a single match.`,
    isCumulative: false,
    target: 3,
    rewardFugazi: 30,
    rewardRankBonus: 3,
    rewardXP: 15
  }
];

const TEMPLATES_TIER_2 = [
  {
    type: 'HOLD_TERRITORY',
    title: 'Iron Curtain',
    desc: (tgt) => `Hold over 60% battlefield control for at least ${tgt}s in a single match.`,
    isCumulative: false,
    targetMin: 35,
    targetMax: 55,
    step: 5,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'HIGH_RANK',
    title: 'Tactical Excellence',
    desc: () => 'Achieve Combat Rating A or higher in a match.',
    isCumulative: false,
    target: 1, // binary
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'PLASMA_DISCIPLINE',
    title: 'Plasma Reserve Control',
    desc: () => 'Win a match without your plasma reserve ever capping at 150.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'RAPID_PUSH',
    title: 'Rapid Incursion',
    desc: () => 'Push the frontline past the enemy 65% threshold within the first 60 seconds.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'SUPERWEAPON_WIN',
    title: 'Tactical Strike',
    desc: () => 'Win a match in which you used your Superweapon.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'OVERWHELMING_FORCE',
    title: 'Overwhelming Force',
    desc: (tgt) => `Field ${tgt} of your own units at the same time.`,
    isCumulative: false,
    targetMin: 12,
    targetMax: 20,
    step: 4,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'SPLASH_CARNAGE',
    title: 'Collateral Shock',
    desc: (tgt) => `Destroy ${tgt} enemy units with Splash or Explosive damage in a single match.`,
    isCumulative: false,
    target: 10,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'TITAN_WARFARE',
    title: 'Titan Warfare',
    desc: () => 'Win a match with a deck containing at least 3 heavy cards (40+ Plasma each).',
    isCumulative: false,
    target: 1,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'LOW_PROFILE',
    title: 'Low Profile Warfare',
    desc: () => 'Win a match with a deck containing no heavy cards (all cards cost under 40 Plasma).',
    isCumulative: false,
    target: 1,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'FORTRESS_PROTOCOL',
    title: 'Fortress Protocol',
    desc: (tgt) => `Deploy at least ${tgt} Structures or Modules in a single match.`,
    isCumulative: false,
    target: 3,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  },
  {
    type: 'SCRAP_HARVEST',
    title: 'Scrap & Plunder',
    desc: (tgt) => `Recover at least ${tgt} Plasma from salvage or plunder using The Salvage Company or Looting Vulture.`,
    isCumulative: false,
    target: 40,
    rewardFugazi: 50,
    rewardRankBonus: 5,
    rewardXP: 25
  }
];

const TEMPLATES_TIER_3 = [
  {
    type: 'S_RANK_VICTORY',
    title: 'Apex Domination',
    desc: () => 'Secure a decisive victory with an S or S+ combat rating.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'CLEAN_SHEET',
    title: 'Impenetrable Bastion',
    desc: () => 'Win a match with at least 85% base integrity remaining.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'COMEBACK_KING',
    title: 'Phoenix Protocol',
    desc: () => 'Turn the tide and claim victory after your base integrity drops below 500 HP.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'UNTOUCHABLE',
    title: 'Untouchable',
    desc: () => 'Win a match without your base taking any damage.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    // Progress mirrors the day's current win run (see recordMatchOutcome()).
    type: 'THREE_PEAT',
    title: 'Three-Peat',
    desc: (tgt) => `Win ${tgt} matches in a row on the same day.`,
    isCumulative: false,
    target: 3,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'UNDERDOG',
    title: 'Underdog',
    desc: () => 'Win against Hard or Insane AI, or win any online match.',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'BLITZKRIEG',
    title: 'Lightning Strike',
    desc: () => 'Secure victory in under 4 minutes and 30 seconds (270s).',
    isCumulative: false,
    target: 1,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  },
  {
    type: 'FUSION_OVERDRIVE',
    title: 'Evolutionary Pinnacle',
    desc: (tgt) => `Perform ${tgt} melds in a single match.`,
    isCumulative: false,
    target: 3,
    rewardFugazi: 80,
    rewardRankBonus: 8,
    rewardXP: 40
  }
];

class DirectivesManager {
  constructor(dateOverride = null) {
    this._customDateOverride = dateOverride;
    this._listeners = new Set();
    this._completionListeners = new Set();
    this._data = this._loadOrGenerate();
    this._streak = this._loadStreak();
    this._reconcileStreak();
  }

  reload() {
    this._data = this._loadOrGenerate();
    this._streak = this._loadStreak();
    this._reconcileStreak();
    this._notify();
  }

  getTodayString() {
    if (this._customDateOverride) {
      return this._customDateOverride;
    }
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setDateOverride(dateStr) {
    this._customDateOverride = dateStr;
    this._data = this._loadOrGenerate();
    this._reconcileStreak();
    this._notify();
  }

  _loadOrGenerate() {
    const today = this.getTodayString();
    const stored = storageAdapter.get('daily_directives', null);

    if (stored && stored.date === today && Array.isArray(stored.directives) && stored.directives.length === 3) {
      if (stored.rerollUsed === undefined) {
        stored.rerollUsed = false;
      }
      return stored;
    }

    // Generate fresh set deterministically for today
    const generated = this.generateForDate(today);
    storageAdapter.set('daily_directives', generated);
    return generated;
  }

  generateForDate(dateStr, seedSuffix = '') {
    const seed = hashString(dateStr + seedSuffix);
    const prng = createPrng(seed);

    const pickTemplate = (pool) => {
      const idx = Math.floor(prng() * pool.length);
      return pool[idx];
    };

    const buildDirective = (tmpl, tier, idSuffix) => {
      let target = tmpl.target || 1;
      if (tmpl.targetMin && tmpl.targetMax) {
        const steps = Math.floor((tmpl.targetMax - tmpl.targetMin) / tmpl.step) + 1;
        target = tmpl.targetMin + Math.floor(prng() * steps) * tmpl.step;
      }

      const race = tmpl.races ? tmpl.races[Math.floor(prng() * tmpl.races.length)] : undefined;

      return {
        id: `daily_${dateStr}${seedSuffix}_t${tier}_${idSuffix}`,
        tier,
        type: tmpl.type,
        title: tmpl.title,
        description: tmpl.desc(target, race),
        ...(race ? { race } : {}),
        isCumulative: tmpl.isCumulative,
        target,
        progress: 0,
        completed: false,
        rewardFugazi: tmpl.rewardFugazi,
        rewardRankBonus: tmpl.rewardRankBonus,
        rewardXP: tmpl.rewardXP
      };
    };

    const d1 = buildDirective(pickTemplate(TEMPLATES_TIER_1), 1, '1');
    const d2 = buildDirective(pickTemplate(TEMPLATES_TIER_2), 2, '2');
    const d3 = buildDirective(pickTemplate(TEMPLATES_TIER_3), 3, '3');

    return {
      date: dateStr,
      directives: [d1, d2, d3],
      jackpotClaimed: false,
      rerollUsed: false,
      winRunToday: 0 // consecutive wins today, feeds THREE_PEAT
    };
  }

  canReroll() {
    return this._data.directives.every(d => d.completed) && !this._data.rerollUsed;
  }

  isRerollUsed() {
    return !!this._data.rerollUsed;
  }

  /** True while at least one of today's quests can still be worked on (or rerolled into new ones). */
  hasOpenQuests() {
    return !this._data.rerollUsed || this.getDirectives().some(d => !d.completed);
  }

  /** Time until the next daily reset (local midnight) as zero-padded parts. */
  getResetCountdown() {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    const diffMs = Math.max(0, midnight - new Date());
    const pad = n => String(n).padStart(2, '0');
    return {
      hours: pad(Math.floor(diffMs / 3600000)),
      mins: pad(Math.floor((diffMs % 3600000) / 60000)),
      secs: pad(Math.floor((diffMs % 60000) / 1000))
    };
  }

  rerollDirectives() {
    if (!this.canReroll()) return false;
    const today = this.getTodayString();
    const rerollSet = this.generateForDate(today, '_reroll');
    this._data.directives = rerollSet.directives;
    this._data.rerollUsed = true;
    this._save();
    return true;
  }

  getDirectives() {
    // Auto-refresh if midnight rolled over
    const today = this.getTodayString();
    if (this._data.date !== today) {
      this._data = this._loadOrGenerate();
      this._reconcileStreak();
      this._notify();
    }
    return this._data.directives;
  }

  isJackpotClaimed() {
    return !!this._data.jackpotClaimed;
  }

  _save() {
    storageAdapter.set('daily_directives', this._data);
    this._notify();
  }

  _notify() {
    for (const listener of this._listeners) {
      try { listener(this._data); } catch (e) { console.error(e); }
    }
  }

  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  addCompletionListener(fn) {
    this._completionListeners.add(fn);
    return () => this._completionListeners.delete(fn);
  }

  _notifyCompletion(directive, jackpotInfo) {
    for (const listener of this._completionListeners) {
      try { listener(directive, jackpotInfo); } catch (e) { console.error(e); }
    }
  }

  // ---------------------------------------------------------------------
  // Daily streak
  // ---------------------------------------------------------------------

  _loadStreak() {
    const stored = storageAdapter.get('daily_streak', null);
    return {
      streak: 0,
      best: 0,
      lastJackpotDate: null,
      freezeUsedWeek: null,
      lastRewardedStreak: 0,
      unseenReward: null,
      ...(stored && typeof stored === 'object' ? stored : {})
    };
  }

  _saveStreak() {
    storageAdapter.set('daily_streak', this._streak);
    this._notify();
  }

  /**
   * Lazily applies missed days: the streak lives when the jackpot was claimed
   * today/yesterday, a single missed day is bridged by the weekly freeze
   * (one per ISO week, consumed automatically), anything else resets it.
   */
  _reconcileStreak() {
    const s = this._streak;
    if (s.streak <= 0 || !s.lastJackpotDate) return;
    const today = this.getTodayString();
    const gap = daysBetween(s.lastJackpotDate, today);
    if (gap <= 1) return;

    if (gap === 2) {
      const missedDay = addDays(s.lastJackpotDate, 1);
      const missedWeek = getISOWeekString(missedDay);
      if (s.freezeUsedWeek !== missedWeek) {
        s.freezeUsedWeek = missedWeek;
        s.lastJackpotDate = missedDay; // the missed day counts as bridged, but adds no +1
        this._saveStreak();
        return;
      }
    }

    s.streak = 0;
    s.lastRewardedStreak = 0;
    this._saveStreak();
  }

  /** Called exactly once per day when the Daily Jackpot is claimed. */
  _advanceStreak() {
    const today = this.getTodayString();
    const s = this._streak;
    if (s.lastJackpotDate === today) return;
    this._reconcileStreak();

    s.streak += 1;
    s.best = Math.max(s.best || 0, s.streak);
    s.lastJackpotDate = today;

    const cycle = Math.floor((s.streak - 1) / STREAK_CYCLE_DAYS) + 1;
    const milestones = getStreakMilestonesForCycle(cycle);
    const milestone = milestones.find(m => m.day === s.streak);
    if (milestone && s.streak > s.lastRewardedStreak) {
      if (milestone.kind === 'fugazi') wallet.addFugazi(milestone.amount, 'daily_streak');
      else inventory.addBoosterPacks(milestone.amount);
      s.lastRewardedStreak = s.streak;
      s.unseenReward = { streak: s.streak, day: milestone.day, kind: milestone.kind, amount: milestone.amount };
    }
    this._saveStreak();
  }

  /** Snapshot for the UI (popup strip, button badge). */
  getStreakInfo() {
    this._reconcileStreak();
    const s = this._streak;
    const today = this.getTodayString();
    const doneToday = s.lastJackpotDate === today && s.streak > 0;

    let displayCycle = 1;
    let cycleProgressDays = 0;

    if (s.streak > 0) {
      if (doneToday) {
        displayCycle = Math.floor((s.streak - 1) / STREAK_CYCLE_DAYS) + 1;
        cycleProgressDays = ((s.streak - 1) % STREAK_CYCLE_DAYS) + 1;
      } else {
        const nextTargetStreak = s.streak + 1;
        displayCycle = Math.floor((nextTargetStreak - 1) / STREAK_CYCLE_DAYS) + 1;
        const prevCycle = Math.floor((s.streak - 1) / STREAK_CYCLE_DAYS) + 1;
        if (displayCycle > prevCycle) {
          cycleProgressDays = 0;
        } else {
          cycleProgressDays = ((s.streak - 1) % STREAK_CYCLE_DAYS) + 1;
        }
      }
    }

    const milestones = getStreakMilestonesForCycle(displayCycle);
    let nextMilestone = milestones.find(m => m.day > s.streak);
    if (!nextMilestone) {
      const nextCycleMilestones = getStreakMilestonesForCycle(displayCycle + 1);
      nextMilestone = nextCycleMilestones[0];
    }
    const daysToNextMilestone = Math.max(0, nextMilestone.day - s.streak);
    const hoursLeft = Number(this.getResetCountdown().hours);
    return {
      streak: s.streak,
      best: s.best || 0,
      doneToday,
      cycle: displayCycle,
      cyclePosition: cycleProgressDays,
      milestones,
      nextMilestone,
      daysToNextMilestone,
      freezeAvailable: s.freezeUsedWeek !== getISOWeekString(today),
      unseenReward: s.unseenReward,
      atRisk: s.streak > 0 && !doneToday && hoursLeft < STREAK_AT_RISK_HOURS
    };
  }

  /** The popup has shown the freshly claimed milestone; stop highlighting it. */
  acknowledgeStreakReward() {
    if (!this._streak.unseenReward) return;
    this._streak.unseenReward = null;
    this._saveStreak();
  }

  /**
   * Records a finished match for THREE_PEAT (consecutive wins on the same day).
   * A win extends today's run, a loss resets it.
   */
  recordMatchOutcome(won) {
    this.getDirectives(); // rolls the day over first
    this._data.winRunToday = won ? (this._data.winRunToday || 0) + 1 : 0;
    for (const d of this._data.directives) {
      if (d.type !== 'THREE_PEAT' || d.completed) continue;
      d.progress = Math.min(d.target, this._data.winRunToday);
      if (d.progress >= d.target) {
        this.completeDirective(d.id);
        return d;
      }
    }
    this._save();
    return null;
  }

  /**
   * Completes a directive, awards Fugazi + XP, and checks for daily jackpot.
   */
  completeDirective(directiveId) {
    const d = this._data.directives.find(x => x.id === directiveId);
    if (!d || d.completed) return false;

    d.completed = true;
    d.progress = d.target;

    // Award Fugazi
    wallet.addFugazi(d.rewardFugazi, 'daily_directive');
    // Award XP - a separate field from rewardRankBonus (that one only
    // nudges the live in-match Gamerank HUD preview, see
    // docs/XP_PROGRESSION_PLAN.md section 0). Guarded with || 0 for any
    // directive generated by an older build before this field existed.
    progress.addXp(d.rewardXP || 0, 'directive');

    // Check all 3 completed for jackpot
    let jackpotClaimedNow = false;
    const allDone = this._data.directives.every(x => x.completed);
    if (allDone && !this._data.jackpotClaimed) {
      this._data.jackpotClaimed = true;
      wallet.addFugazi(DAILY_JACKPOT_FUGAZI, 'daily_jackpot');
      progress.addXp(DAILY_JACKPOT_XP, 'daily_jackpot');
      progress.recordDailyJackpot();
      this._advanceStreak();
      jackpotClaimedNow = true;
    }

    this._save();
    this._notifyCompletion(d, {
      jackpot: jackpotClaimedNow,
      fugazi: DAILY_JACKPOT_FUGAZI,
      xp: DAILY_JACKPOT_XP
    });
    return true;
  }

  /**
   * Ingests cumulative progress made during a match (e.g. total damage dealt, matches won).
   */
  addProgress(type, amount = 1, meta = null) {
    let changed = false;
    for (const d of this._data.directives) {
      if (d.type === type && !d.completed && d.isCumulative && (!d.race || d.race === meta?.race)) {
        d.progress = Math.min(d.target, d.progress + amount);
        changed = true;
        if (d.progress >= d.target) {
          this.completeDirective(d.id);
        }
      }
    }
    if (changed) {
      this._save();
    }
  }
}

/**
 * In-Match Zero-GC Directive Tracker.
 * Evaluates tactical match conditions without allocating objects during game ticks.
 */
export class MatchDirectiveTracker {
  constructor(myTeam = 'western') {
    this.myTeam = myTeam;
    this.reset();
  }

  reset() {
    this.initialHp = null;
    this.lowestBaseHp = 1500;
    this.minBaseHp = Infinity; // uncapped low-water mark (UNTOUCHABLE)
    this.peakOwnUnits = 0; // most own mobile units alive at once (OVERWHELMING_FORCE)
    this.plasmaEverCapped = false;
    this.duration = 0;
    this.maxTerritoryFront = 0.5;
    this.rapidPushCompleted = false;
    this.secAbove60Territory = 0;
    this.matchCompleted = false;
    this.deckHeavyCount = 0;
    this.deckMaxCost = 0;
    this.deckProfileRecorded = false;
    this.superweaponsUsed = 0;
  }

  update(dt, battlefield, currentPlasma) {
    if (!battlefield) return;

    const teamStats = battlefield.stats?.teams?.[this.myTeam];
    if (teamStats) {
      if (teamStats.deckProfileRecorded || teamStats.deckMaxCost > 0 || teamStats.deckHeavyCount > 0) {
        this.deckHeavyCount = teamStats.deckHeavyCount || 0;
        this.deckMaxCost = teamStats.deckMaxCost || 0;
        this.deckProfileRecorded = true;
      }
      this.superweaponsUsed = teamStats.superweaponsUsed || 0;
    }

    const myBase = battlefield.bases?.[this.myTeam];
    if (myBase) {
      if (this.initialHp === null) this.initialHp = myBase.hp;
      if (myBase.hp < this.lowestBaseHp) {
        this.lowestBaseHp = myBase.hp;
      }
      if (myBase.hp < this.minBaseHp) {
        this.minBaseHp = myBase.hp;
      }
    }

    // Zero-GC: plain index loop, no filter/map (runs every frame)
    const units = battlefield.units;
    if (units) {
      let own = 0;
      for (let i = 0; i < units.length; i++) {
        const u = units[i];
        if (u.team === this.myTeam && !u.isDead && !u.isStructure) own++;
      }
      if (own > this.peakOwnUnits) this.peakOwnUnits = own;
    }

    // Check plasma cap (150)
    if (currentPlasma >= 150) {
      this.plasmaEverCapped = true;
    }

    // Check territory line (instantaneous frontline with defensive fallback)
    const duration = battlefield.stats?.duration ?? (this.duration + dt);
    this.duration = duration;

    const width = battlefield.width || 1600;
    let instantFront = 0.5;
    if (battlefield.territoryDivider !== undefined && battlefield.territoryDivider !== null && width > 0) {
      instantFront = this.myTeam === 'western'
        ? (battlefield.territoryDivider / width)
        : (1.0 - (battlefield.territoryDivider / width));
    } else if (battlefield.stats?.teams) {
      // Defensive fallback for mocks without territoryDivider
      const westernTerr = battlefield.stats.teams.western?.territoryControlTimeIntegral || 0;
      const easternTerr = battlefield.stats.teams.eastern?.territoryControlTimeIntegral || 0;
      const totalTerr = westernTerr + easternTerr;
      if (totalTerr > 0) {
        instantFront = (this.myTeam === 'western' ? westernTerr : easternTerr) / totalTerr;
      }
    }

    if (instantFront > 0.60) {
      this.secAbove60Territory += dt;
    }
    if (instantFront > this.maxTerritoryFront) {
      this.maxTerritoryFront = instantFront;
    }
    // Rapid push within 60s
    if (duration <= 60 && instantFront >= 0.65) {
      this.rapidPushCompleted = true;
    }
  }
}

export const directives = new DirectivesManager();
export { DirectivesManager };
